import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, UserRole } from './hooks/useAuth';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import LocationTracking from './pages/LocationTracking';
import RevolutionaryDashboard from './pages/RevolutionaryDashboard';
import Security from './pages/Security';
import PromptPay from './pages/PromptPay';
import Wallet from './pages/Wallet';
import Affiliate from './pages/Affiliate';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Orders from './pages/Orders';
import Products from './pages/Products';
import POS from './pages/POS';
import Analytics from './pages/Analytics';
import Campaigns from './pages/Campaigns';
import Reports from './pages/Reports';
import Support from './pages/Support';
import Monitor from './pages/Monitor';
import Audit from './pages/Audit';
import API from './pages/API';

function App() {
  // Define role-based access
  const masterAdminRoutes: UserRole[] = ["superadmin"];
  const adminRoutes: UserRole[] = ["superadmin", "admin"];
  const merchantRoutes: UserRole[] = ["superadmin", "admin", "merchant"];

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="dashboard">
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/location-tracking" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="dashboard">
              <LocationTracking />
            </ProtectedRoute>
          } />
          <Route path="/revolutionary" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="dashboard">
              <MainLayout>
                <RevolutionaryDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/security" element={
            <ProtectedRoute requiredRoles={masterAdminRoutes} requiredPermission="security">
              <MainLayout>
                <Security />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/promptpay" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="payments">
              <MainLayout>
                <PromptPay />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="dashboard">
              <MainLayout>
                <Wallet />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/affiliate-links" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="dashboard">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Affiliate Links</h1>
                  <p className="text-gray-600 mt-2">Recruit vendors and earn commissions</p>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 mt-6">
                    <h2 className="text-lg font-semibold text-green-900">Your Affiliate Link</h2>
                    <div className="mt-4 flex items-center gap-4">
                      <input
                        type="text"
                        value="https://yourapp.com/become-merchant?ref=merchant123"
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        Copy Link
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-green-700">
                      Share this link with vendors to recruit them. Earn 10% commission on their first 3 months!
                    </p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900">Recruitment Stats</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="text-sm text-gray-500">Vendors Recruited</div>
                        <div className="text-2xl font-bold text-gray-900 mt-2">12</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="text-sm text-gray-500">Commission Earned</div>
                        <div className="text-2xl font-bold text-gray-900 mt-2">THB 4,500</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="text-sm text-gray-500">Active Vendors</div>
                        <div className="text-2xl font-bold text-gray-900 mt-2">8</div>
                      </div>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/affiliate" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="dashboard">
              <Affiliate />
            </ProtectedRoute>
          } />
          <Route path="/tenants" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="tenants">
              <MainLayout>
                <Users />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="settings">
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="ecommerce">
              <MainLayout>
                <Orders />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/products" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="ecommerce">
              <MainLayout>
                <Products />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="analytics">
              <MainLayout>
                <Analytics />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="dashboard">
              <MainLayout>
                <Campaigns />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="reports">
              <MainLayout>
                <Reports />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="support">
              <MainLayout>
                <Support />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/monitor" element={
            <ProtectedRoute requiredRoles={masterAdminRoutes} requiredPermission="all">
              <MainLayout>
                <Monitor />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/audit" element={
            <ProtectedRoute requiredRoles={masterAdminRoutes} requiredPermission="support">
              <MainLayout>
                <Audit />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/api" element={
            <ProtectedRoute requiredRoles={masterAdminRoutes} requiredPermission="all">
              <MainLayout>
                <API />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/become-merchant" element={
            <ProtectedRoute requiredRoles={["customer"]} requiredPermission="vendor">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Become a Merchant</h1>
                  <p className="text-gray-600 mt-2">Upgrade your account to start selling products</p>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
                    <h2 className="text-lg font-semibold text-blue-900">Merchant Benefits</h2>
                    <ul className="mt-4 space-y-2 text-blue-800">
                      <li> Sell products to customers</li>
                      <li> Accept PromptPay payments</li>
                      <li> Access analytics dashboard</li>
                      <li> Manage inventory</li>
                      <li> Generate affiliate links</li>
                    </ul>
                  </div>

                  <button className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
                    Apply for Merchant Account
                  </button>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute requiredPermission="payments">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
                  <p className="text-gray-600 mt-2">Payment management interface</p>

                  {/* Payment Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Total Processed</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">฿1,234,567</div>
                      <div className="text-sm text-green-600 mt-1">+15.3%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Pending</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">฿45,230</div>
                      <div className="text-sm text-yellow-600 mt-1">23 transactions</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Failed</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">฿12,450</div>
                      <div className="text-sm text-red-600 mt-1">8 transactions</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Success Rate</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">98.2%</div>
                      <div className="text-sm text-green-600 mt-1">Above target</div>
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h2>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TXN-001</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿2,500.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Completed</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Credit Card</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TXN-002</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿1,750.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500 text-white">Pending</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Bank Transfer</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TXN-003</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿3,200.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Completed</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-20</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Credit Card</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TXN-004</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿890.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500 text-white">Failed</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-20</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Digital Wallet</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/pos" element={
            <ProtectedRoute requiredRoles={merchantRoutes} requiredPermission="ecommerce" role="merchant">
              <MainLayout>
                <POS />
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/ecommerce" element={
            <ProtectedRoute requiredPermission="ecommerce">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">E-commerce</h1>
                  <p className="text-gray-600 mt-2">Product and order management</p>

                  {/* E-commerce Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Total Orders</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">1,847</div>
                      <div className="text-sm text-green-600 mt-1">+23.5%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Products</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">342</div>
                      <div className="text-sm text-blue-600 mt-1">12 new</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Revenue</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">฿4.5M</div>
                      <div className="text-sm text-green-600 mt-1">+18.2%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Conversion Rate</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">3.2%</div>
                      <div className="text-sm text-yellow-600 mt-1">-0.5%</div>
                    </div>
                  </div>

                  {/* Recent Orders */}
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h2>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#ORD-7823</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">John Doe</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿2,450.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Delivered</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#ORD-7822</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Jane Smith</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿1,890.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500 text-white">Shipped</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#ORD-7821</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Mike Johnson</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿3,200.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500 text-white">Processing</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-20</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#ORD-7820</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Sarah Williams</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿560.00</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Delivered</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-20</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Top Products */}
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Product Name</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">Wireless Headphones</div>
                        <div className="text-sm text-gray-600 mt-1">234 sold</div>
                        <div className="text-sm font-bold text-green-600 mt-2">฿58,500</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Product Name</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">Smart Watch Pro</div>
                        <div className="text-sm text-gray-600 mt-1">189 sold</div>
                        <div className="text-sm font-bold text-green-600 mt-2">฿45,360</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Product Name</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">USB-C Hub</div>
                        <div className="text-sm text-gray-600 mt-1">156 sold</div>
                        <div className="text-sm font-bold text-green-600 mt-2">฿23,400</div>
                      </div>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/tenants" element={
            <ProtectedRoute requiredPermission="tenants">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
                  <p className="text-gray-600 mt-2">Manage tenants and their configurations</p>

                  {/* Tenant Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Total Tenants</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">452</div>
                      <div className="text-sm text-green-600 mt-1">+12 new</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Active</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">423</div>
                      <div className="text-sm text-green-600 mt-1">93.6%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Inactive</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">29</div>
                      <div className="text-sm text-yellow-600 mt-1">6.4%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Pending Approval</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">5</div>
                      <div className="text-sm text-blue-600 mt-1">Requires action</div>
                    </div>
                  </div>

                  {/* Tenant List */}
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Tenant List</h2>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TNT-001</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Acme Corporation</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Enterprise</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Active</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-01-15</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TNT-002</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">TechStart Inc</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Professional</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">Active</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-02-20</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TNT-003</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Global Retail Ltd</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Basic</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500 text-white">Inactive</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-03-10</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#TNT-004</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Startup XYZ</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Professional</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500 text-white">Pending</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-18</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute requiredPermission="inventory">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
                  <p className="text-gray-600 mt-2">Track and manage product inventory</p>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Total Products</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">1,234</div>
                      <div className="text-sm text-green-600 mt-1">+45 new</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">In Stock</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">987</div>
                      <div className="text-sm text-green-600 mt-1">80%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Low Stock</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">189</div>
                      <div className="text-sm text-yellow-600 mt-1">Alert</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Out of Stock</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">58</div>
                      <div className="text-sm text-red-600 mt-1">Urgent</div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Inventory Changes</h2>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Wireless Headphones</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">SKU-001</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">234</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500 text-white">In Stock</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Smart Watch Pro</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">SKU-002</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">12</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500 text-white">Low Stock</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2026-04-21</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute requiredRoles={adminRoutes} requiredPermission="analytics">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
                  <p className="text-gray-600 mt-2">Performance metrics and insights</p>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Page Views</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">45.2K</div>
                      <div className="text-sm text-green-600 mt-1">+18.5%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Unique Visitors</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">12.8K</div>
                      <div className="text-sm text-green-600 mt-1">+12.3%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Bounce Rate</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">32.4%</div>
                      <div className="text-sm text-green-600 mt-1">-5.2%</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <div className="text-sm text-gray-500">Avg Session</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">4m 32s</div>
                      <div className="text-sm text-green-600 mt-1">+8.7%</div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Traffic Sources</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Organic Search</div>
                        <div className="text-xl font-bold text-gray-900 mt-2">45%</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Direct</div>
                        <div className="text-xl font-bold text-gray-900 mt-2">28%</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Social</div>
                        <div className="text-xl font-bold text-gray-900 mt-2">18%</div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow">
                        <div className="text-sm text-gray-500">Referral</div>
                        <div className="text-xl font-bold text-gray-900 mt-2">9%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredPermission="reports">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                  <p className="text-gray-600 mt-2">Generate and view business reports</p>

                  <div className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Reports</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="text-sm text-gray-500">Report Type</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">Sales Report</div>
                        <div className="text-sm text-gray-600 mt-1">Monthly sales performance</div>
                        <button className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors">Generate</button>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="text-sm text-gray-500">Report Type</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">Inventory Report</div>
                        <div className="text-sm text-gray-600 mt-1">Stock levels and movements</div>
                        <button className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors">Generate</button>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="text-sm text-gray-500">Report Type</div>
                        <div className="text-base font-semibold text-gray-900 mt-2">Customer Report</div>
                        <div className="text-sm text-gray-600 mt-1">Customer analytics and insights</div>
                        <button className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors">Generate</button>
                      </div>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin-settings" element={
            <ProtectedRoute requiredRoles={masterAdminRoutes} requiredPermission="security">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
                  <p className="text-gray-600 mt-2">System configuration and feature management</p>

                  {/* Feature Toggles */}
                  <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Feature Controls</h2>

                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">PromptPay Payments</h3>
                          <p className="text-sm text-gray-600 mt-1">Enable/disable PromptPay payment processing</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">OCR Processing</h3>
                          <p className="text-sm text-gray-600 mt-1">Enable/disable receipt OCR verification</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">Merchant Registration</h3>
                          <p className="text-sm text-gray-600 mt-1">Allow new merchants to register</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Rate Controls */}
                  <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Transaction Rates</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Deposit Fee Rate (%)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            defaultValue="2.5"
                            min="0"
                            max="10"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Fee charged on deposits</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Withdrawal Fee Rate (%)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            defaultValue="1.5"
                            min="0"
                            max="10"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Fee charged on withdrawals</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Merchant Commission Rate (%)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            defaultValue="10"
                            min="0"
                            max="50"
                            step="0.5"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Commission for recruiting merchants</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transaction Limit (THB)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            defaultValue="50000"
                            min="1000"
                            max="1000000"
                            step="1000"
                          />
                          <span className="text-sm text-gray-600">THB</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Maximum transaction amount</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="mt-8 flex justify-end gap-4">
                    <button className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                      Reset to Defaults
                    </button>
                    <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Save Settings
                    </button>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredPermission="settings">
              <MainLayout>
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                  <p className="text-gray-600 mt-2">Configure application settings</p>

                  <div className="mt-8 space-y-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Email Notifications</div>
                            <div className="text-xs text-gray-500">Receive email alerts for important events</div>
                          </div>
                          <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors">Enabled</button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Two-Factor Authentication</div>
                            <div className="text-xs text-gray-500">Add an extra layer of security</div>
                          </div>
                          <button className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-xl hover:bg-gray-300 transition-colors">Disabled</button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Settings</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <input type="text" defaultValue="AEGD Corporation" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                          <input type="email" defaultValue="admin@aegd.com" className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors">Save Changes</button>
                      </div>
                    </div>
                  </div>
                </div>
              </MainLayout>
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
